import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  CircleNotch,
  TrendUp,
  TrendDown,
  Scales,
  FileText,
  Download,
  CalendarBlank,
  ChartLine,
  Bank,
  Wallet,
  Calculator
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import ReportToolbar from '../../components/ReportToolbar';
import { DrilldownDialog } from '../../components/reports/DrilldownDialog';
import { DataNote } from '../../components/reports/DataNote';
import { ChartCard, TrendBarChart, DonutChart } from '../../components/reports/FinanceCharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinancialReportsPage() {
  const { hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState('profit-loss');
  const [loading, setLoading] = useState(true);
  
  // P&L State
  const [plData, setPlData] = useState(null);
  const [plStartDate, setPlStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
  );
  const [plEndDate, setPlEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  
  // Balance Sheet State
  const [bsData, setBsData] = useState(null);
  const [bsAsOfDate, setBsAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Trial Balance State
  const [tbData, setTbData] = useState(null);
  const [tbAsOfDate, setTbAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const canView = hasPermission('accounts', 'view');

  const [drill, setDrill] = useState(null);
  const [plTrend, setPlTrend] = useState([]);
  const drillPL = (category, title, accountId) =>
    setDrill({ category, title, accountId, startDate: plStartDate, endDate: plEndDate });
  const drillBS = (category, title, accountId) =>
    setDrill({ category, title, accountId, endDate: bsAsOfDate });
  const CLICKABLE = 'cursor-pointer hover:ring-2 hover:ring-primary/40 hover:-translate-y-0.5 transition-[transform,box-shadow]';

  // Hidden Expenses banner — accounts that *look* like expenses but are mistyped
  // are silently excluded from the P&L. Surface them right where they hurt.
  const [hiddenExpenses, setHiddenExpenses] = useState([]);
  const fetchHiddenExpenses = async () => {
    try {
      const res = await axios.get(`${API_URL}/coa/hidden-expenses`);
      setHiddenExpenses(res.data?.hidden_expenses || []);
    } catch {
      setHiddenExpenses([]);
    }
  };
  useEffect(() => {
    fetchHiddenExpenses();
    const onCoaUpdated = () => fetchHiddenExpenses();
    window.addEventListener('coa:updated', onCoaUpdated);
    return () => window.removeEventListener('coa:updated', onCoaUpdated);
  }, []);

  useEffect(() => {
    if (activeTab === 'profit-loss') fetchProfitLoss();
    else if (activeTab === 'balance-sheet') fetchBalanceSheet();
    else if (activeTab === 'trial-balance') fetchTrialBalance();
  }, [activeTab]);

  const fetchTrialBalance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tbAsOfDate) params.append('as_of_date', tbAsOfDate);
      const response = await axios.get(`${API_URL}/reports/trial-balance?${params}`);
      setTbData(response.data);
    } catch (error) {
      const raw = error?.response?.data?.detail;
      let msg = 'Failed to fetch Trial Balance';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || JSON.stringify(raw);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitLoss = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (plStartDate) params.append('start_date', plStartDate);
      if (plEndDate) params.append('end_date', plEndDate);
      
      const [response, trend] = await Promise.all([
        axios.get(`${API_URL}/reports/profit-loss?${params}`),
        axios.get(`${API_URL}/reports/drilldown?category=pnl&${params}`).catch(() => null),
      ]);
      setPlData(response.data);
      setPlTrend((trend?.data?.monthly || []).map((m) => ({ ...m, income: m.income || 0, expense: m.expense || 0 })));
    } catch (error) {
      toast.error('Failed to fetch Profit & Loss');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (bsAsOfDate) params.append('as_of_date', bsAsOfDate);
      
      const response = await axios.get(`${API_URL}/reports/balance-sheet?${params}`);
      setBsData(response.data);
    } catch (error) {
      toast.error('Failed to fetch Balance Sheet');
    } finally {
      setLoading(false);
    }
  };

  const handlePLRefresh = () => {
    fetchProfitLoss();
  };

  const handleBSRefresh = () => {
    fetchBalanceSheet();
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">You don't have permission to view financial reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Financial Reports</h1>
          <p className="text-muted-foreground">Trial Balance, Profit &amp; Loss and Balance Sheet</p>
        </div>
      </motion.div>

      <DataNote />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="profit-loss" className="gap-2" data-testid="pl-tab">
            <ChartLine size={16} />
            Profit &amp; Loss
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="gap-2" data-testid="bs-tab">
            <Scales size={16} />
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="trial-balance" className="gap-2" data-testid="tb-tab">
            <Calculator size={16} />
            Trial Balance
          </TabsTrigger>
        </TabsList>

        {/* Profit & Loss Tab */}
        <TabsContent value="profit-loss" className="mt-6">
          {/* Hidden-Expenses warning — surfaces COA mistypes that silently
              suppress lines from the P&L. */}
          {hiddenExpenses.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 mb-4 flex flex-wrap items-start gap-3"
              data-testid="pl-hidden-expenses-banner"
            >
              <TrendDown size={22} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[240px]">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  {hiddenExpenses.length} expense-like account{hiddenExpenses.length === 1 ? '' : 's'} {hiddenExpenses.length === 1 ? 'is' : 'are'} mistyped and excluded from this P&amp;L
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  These accounts look like expenses (by code, name or voucher usage) but are recorded as{' '}
                  <span className="font-mono">{[...new Set(hiddenExpenses.map(h => h.current_type))].join(', ')}</span>{' '}
                  in your Chart of Accounts, so every debit silently lands on the Balance Sheet instead.
                </p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {hiddenExpenses.slice(0, 8).map(h => (
                    <li key={h.account_id} className="text-[11px] bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">
                      <span className="font-mono">{h.code}</span> {h.name}
                    </li>
                  ))}
                  {hiddenExpenses.length > 8 && (
                    <li className="text-[11px] text-muted-foreground self-center">+ {hiddenExpenses.length - 8} more</li>
                  )}
                </ul>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500/40"
                onClick={() => window.location.assign('/dashboard/accounts')}
                data-testid="pl-fix-coa-btn"
              >
                Fix in Chart of Accounts →
              </Button>
            </motion.div>
          )}

          {/* Date Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-4 mb-6"
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">From Date</Label>
                <Input
                  type="date"
                  value={plStartDate}
                  onChange={(e) => setPlStartDate(e.target.value)}
                  className="w-40"
                  data-testid="pl-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">To Date</Label>
                <Input
                  type="date"
                  value={plEndDate}
                  onChange={(e) => setPlEndDate(e.target.value)}
                  className="w-40"
                  data-testid="pl-end-date"
                />
              </div>
              <Button onClick={handlePLRefresh} disabled={loading} data-testid="pl-refresh">
                {loading ? <CircleNotch className="animate-spin" size={16} /> : 'Apply'}
              </Button>
            </div>
          </motion.div>

          {/* Universal export toolbar */}
          <ReportToolbar
            testidPrefix="pl-toolbar"
            downloadBaseName={`profit-loss-${plStartDate}_to_${plEndDate}`}
            exportUrl={(fmt) => `${API_URL}/reports/profit-loss.${fmt}?start_date=${plStartDate}&end_date=${plEndDate}`}
          />

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <CircleNotch className="animate-spin text-primary" size={32} />
            </div>
          ) : plData ? (
            <div className="grid gap-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 border-l-emerald-500 ${CLICKABLE}`}
                  onClick={() => drillPL('income', 'Total Income — approved invoices & income entries')}
                  data-testid="pl-card-income"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <TrendUp size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Income</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(plData.income.total)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 border-l-red-500 ${CLICKABLE}`}
                  onClick={() => drillPL('expense', 'Total Expenses — bills, payments & expense entries')}
                  data-testid="pl-card-expenses"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <TrendDown size={20} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Expenses</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(plData.expenses.total)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 ${plData.is_profit ? 'border-l-emerald-500' : 'border-l-red-500'} ${CLICKABLE}`}
                  onClick={() => drillPL('pnl', `Net ${plData.is_profit ? 'Profit' : 'Loss'} — income less expenses`)}
                  data-testid="pl-card-net"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${plData.is_profit ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <Scales size={20} className={plData.is_profit ? 'text-emerald-600' : 'text-red-600'} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Net {plData.is_profit ? 'Profit' : 'Loss'}
                      </p>
                      <p className={`text-xl font-bold ${plData.is_profit ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(Math.abs(plData.net_profit))}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <ChartCard title="Income vs Expenses by month" subtitle="Click any summary card for the entries behind it" className="lg:col-span-2" testid="pl-trend-chart">
                  <TrendBarChart data={plTrend} series={[{ key: 'income', label: 'Income', color: '#059669' }, { key: 'expense', label: 'Expenses', color: '#dc2626' }]} />
                </ChartCard>
                <ChartCard title="Expense breakdown" subtitle="Share of total expenses" testid="pl-expense-chart">
                  <DonutChart
                    data={plData.expenses.accounts.map((a) => ({ name: a.account_name, value: a.amount, id: a.account_id }))}
                    onSliceClick={(d) => d?.id && drillPL('expense', d.name, d.id)}
                  />
                </ChartCard>
              </div>

              {/* Income Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-tactile rounded-lg overflow-hidden"
              >
                <div className="p-4 border-b bg-emerald-50 dark:bg-emerald-950/20">
                  <h3 className="font-semibold flex items-center gap-2">
                    <TrendUp size={18} className="text-emerald-600" />
                    Income / Revenue
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead className="text-right w-40">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plData.income.accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                          No income recorded for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {plData.income.accounts.map((acc) => (
                          <TableRow key={acc.account_id} className="cursor-pointer hover:bg-emerald-500/5" onClick={() => drillPL('income', `${acc.account_code} · ${acc.account_name}`, acc.account_id)} data-testid={`pl-income-row-${acc.account_code}`}>
                            <TableCell className="font-mono text-sm">{acc.account_code}</TableCell>
                            <TableCell>{acc.account_name}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(acc.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-emerald-50 dark:bg-emerald-950/20 font-bold">
                          <TableCell colSpan={2}>Total Income</TableCell>
                          <TableCell className="text-right text-emerald-600">{formatCurrency(plData.income.total)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </motion.div>

              {/* Expenses Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-tactile rounded-lg overflow-hidden"
              >
                <div className="p-4 border-b bg-red-50 dark:bg-red-950/20">
                  <h3 className="font-semibold flex items-center gap-2">
                    <TrendDown size={18} className="text-red-600" />
                    Expenses
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead className="text-right w-40">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plData.expenses.accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                          No expenses recorded for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {plData.expenses.accounts.map((acc) => (
                          <TableRow key={acc.account_id} className="cursor-pointer hover:bg-red-500/5" onClick={() => drillPL('expense', `${acc.account_code} · ${acc.account_name}`, acc.account_id)} data-testid={`pl-expense-row-${acc.account_code}`}>
                            <TableCell className="font-mono text-sm">{acc.account_code}</TableCell>
                            <TableCell>{acc.account_name}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(acc.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-red-50 dark:bg-red-950/20 font-bold">
                          <TableCell colSpan={2}>Total Expenses</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(plData.expenses.total)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </motion.div>

              {/* Net Profit/Loss Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={`card-tactile rounded-lg p-6 text-center ${plData.is_profit ? 'bg-emerald-50 dark:bg-emerald-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}
              >
                <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
                  Net {plData.is_profit ? 'Profit' : 'Loss'} for Period
                </p>
                <p className={`text-4xl font-bold ${plData.is_profit ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(plData.net_profit))}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {formatDate(plData.period.start_date)} - {formatDate(plData.period.end_date)}
                </p>
              </motion.div>
            </div>
          ) : null}
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance-sheet" className="mt-6">
          {/* Date Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg p-4 mb-6"
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">As of Date</Label>
                <Input
                  type="date"
                  value={bsAsOfDate}
                  onChange={(e) => setBsAsOfDate(e.target.value)}
                  className="w-40"
                  data-testid="bs-as-of-date"
                />
              </div>
              <Button onClick={handleBSRefresh} disabled={loading} data-testid="bs-refresh">
                {loading ? <CircleNotch className="animate-spin" size={16} /> : 'Apply'}
              </Button>
            </div>
          </motion.div>

          {/* Universal export toolbar */}
          <ReportToolbar
            testidPrefix="bs-toolbar"
            downloadBaseName={`balance-sheet-${bsAsOfDate}`}
            exportUrl={(fmt) => `${API_URL}/reports/balance-sheet.${fmt}?as_of_date=${bsAsOfDate}`}
          />

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <CircleNotch className="animate-spin text-primary" size={32} />
            </div>
          ) : bsData ? (
            <div className="grid gap-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 border-l-blue-500 ${CLICKABLE}`}
                  onClick={() => drillBS('asset', 'Total Assets — cash, bank, receivables & other assets')}
                  data-testid="bs-card-assets"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <Wallet size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Assets</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(bsData.assets.total)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 border-l-red-500 ${CLICKABLE}`}
                  onClick={() => drillBS('liability', 'Total Liabilities — payables, VAT & other liabilities')}
                  data-testid="bs-card-liabilities"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <Bank size={20} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Liabilities</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(bsData.liabilities.total)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 border-l-purple-500 ${CLICKABLE}`}
                  onClick={() => drillBS('equity_re', 'Total Equity — capital, equity entries & retained earnings')}
                  data-testid="bs-card-equity"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <Scales size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Equity</p>
                      <p className="text-xl font-bold text-purple-600">{formatCurrency(bsData.equity.total)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={`card-tactile rounded-lg p-5 border-l-4 ${bsData.is_balanced ? 'border-l-emerald-500' : 'border-l-amber-500'} ${CLICKABLE}`}
                  onClick={() => setActiveTab('trial-balance')}
                  data-testid="bs-card-balance-check"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${bsData.is_balanced ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                      <Scales size={20} className={bsData.is_balanced ? 'text-emerald-600' : 'text-amber-600'} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance Check</p>
                      <p className={`text-sm font-bold ${bsData.is_balanced ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {bsData.is_balanced ? 'Balanced' : 'Unbalanced'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <ChartCard title="Assets = Liabilities + Equity" subtitle="Click a card above for the entries behind it" testid="bs-equation-chart">
                  <TrendBarChart
                    isMonth={false}
                    xKey="name"
                    data={[{ name: 'Assets', assets: bsData.assets.total }, { name: 'Liab. + Equity', liabilities: bsData.liabilities.total, equity: bsData.equity.total }]}
                    series={[{ key: 'assets', label: 'Assets', color: '#2563eb' }, { key: 'liabilities', label: 'Liabilities', color: '#dc2626' }, { key: 'equity', label: 'Equity', color: '#7c3aed' }]}
                  />
                </ChartCard>
                <ChartCard title="Asset composition" testid="bs-assets-chart">
                  <DonutChart data={bsData.assets.accounts.map((a) => ({ name: a.account_name, value: a.amount, id: a.account_id }))}
                    onSliceClick={(d) => d?.id && drillBS('asset', d.name, d.id)} />
                </ChartCard>
                <ChartCard title="Liabilities & equity composition" testid="bs-liab-chart">
                  <DonutChart data={[...bsData.liabilities.accounts, ...bsData.equity.accounts].map((a) => ({ name: a.account_name, value: a.amount }))} />
                </ChartCard>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Assets Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="card-tactile rounded-lg overflow-hidden"
                >
                  <div className="p-4 border-b bg-blue-50 dark:bg-blue-950/20">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Wallet size={18} className="text-blue-600" />
                      Assets
                    </h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Code</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right w-32">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bsData.assets.accounts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                            No assets recorded
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {bsData.assets.accounts.map((acc) => (
                            <TableRow key={acc.account_id} className="cursor-pointer hover:bg-blue-500/5" onClick={() => drillBS('asset', `${acc.account_code} · ${acc.account_name}`, acc.account_id)} data-testid={`bs-asset-row-${acc.account_code}`}>
                              <TableCell className="font-mono text-xs">{acc.account_code}</TableCell>
                              <TableCell className="text-sm">{acc.account_name}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(acc.amount)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-blue-50 dark:bg-blue-950/20 font-bold">
                            <TableCell colSpan={2}>Total Assets</TableCell>
                            <TableCell className="text-right text-blue-600">{formatCurrency(bsData.assets.total)}</TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </motion.div>

                {/* Liabilities & Equity Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-4"
                >
                  {/* Liabilities */}
                  <div className="card-tactile rounded-lg overflow-hidden">
                    <div className="p-4 border-b bg-red-50 dark:bg-red-950/20">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Bank size={18} className="text-red-600" />
                        Liabilities
                      </h3>
                    </div>
                    <Table>
                      <TableBody>
                        {bsData.liabilities.accounts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                              No liabilities recorded
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {bsData.liabilities.accounts.map((acc) => (
                              <TableRow key={acc.account_id} className="cursor-pointer hover:bg-red-500/5" onClick={() => drillBS('liability', `${acc.account_code} · ${acc.account_name}`, acc.account_id)} data-testid={`bs-liability-row-${acc.account_code}`}>
                                <TableCell className="font-mono text-xs w-20">{acc.account_code}</TableCell>
                                <TableCell className="text-sm">{acc.account_name}</TableCell>
                                <TableCell className="text-right font-medium w-32">{formatCurrency(acc.amount)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-red-50 dark:bg-red-950/20 font-bold">
                              <TableCell colSpan={2}>Total Liabilities</TableCell>
                              <TableCell className="text-right text-red-600">{formatCurrency(bsData.liabilities.total)}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Equity */}
                  <div className="card-tactile rounded-lg overflow-hidden">
                    <div className="p-4 border-b bg-purple-50 dark:bg-purple-950/20">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Scales size={18} className="text-purple-600" />
                        Equity
                      </h3>
                    </div>
                    <Table>
                      <TableBody>
                        {bsData.equity.accounts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                              No equity recorded
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {bsData.equity.accounts.map((acc) => (
                              <TableRow
                                key={acc.account_id}
                                className="cursor-pointer hover:bg-purple-500/5"
                                onClick={() => (acc.account_id === 'retained_earnings'
                                  ? drillBS('pnl', 'Retained Earnings — income less expenses')
                                  : drillBS('equity', `${acc.account_code} · ${acc.account_name}`, acc.account_id))}
                                data-testid={`bs-equity-row-${acc.account_code}`}
                              >
                                <TableCell className="font-mono text-xs w-20">{acc.account_code}</TableCell>
                                <TableCell className="text-sm">{acc.account_name}</TableCell>
                                <TableCell className="text-right font-medium w-32">{formatCurrency(acc.amount)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-purple-50 dark:bg-purple-950/20 font-bold">
                              <TableCell colSpan={2}>Total Equity</TableCell>
                              <TableCell className="text-right text-purple-600">{formatCurrency(bsData.equity.total)}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Total Liabilities + Equity */}
                  <div className="card-tactile rounded-lg p-4 bg-gradient-to-r from-red-50 to-purple-50 dark:from-red-950/20 dark:to-purple-950/20">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Liabilities + Equity</span>
                      <span className="text-xl font-bold">{formatCurrency(bsData.total_liabilities_and_equity)}</span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Balance Equation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className={`card-tactile rounded-lg p-6 text-center ${bsData.is_balanced ? 'bg-emerald-50 dark:bg-emerald-950/20' : 'bg-amber-50 dark:bg-amber-950/20'}`}
              >
                <p className="text-sm uppercase tracking-wide text-muted-foreground mb-4">
                  Balance Sheet Equation
                </p>
                <div className="flex items-center justify-center gap-4 text-lg">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Assets</p>
                    <p className="font-bold text-blue-600">{formatCurrency(bsData.assets.total)}</p>
                  </div>
                  <span className="text-2xl font-bold">=</span>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Liabilities</p>
                    <p className="font-bold text-red-600">{formatCurrency(bsData.liabilities.total)}</p>
                  </div>
                  <span className="text-2xl font-bold">+</span>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Equity</p>
                    <p className="font-bold text-purple-600">{formatCurrency(bsData.equity.total)}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  As of {formatDate(bsData.as_of_date)}
                </p>
              </motion.div>
            </div>
          ) : null}
        </TabsContent>

        {/* Trial Balance Tab */}
        <TabsContent value="trial-balance" className="mt-6">
          {/* Date Filter */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border rounded-lg p-4 mb-6"
          >
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="tb-as-of" className="flex items-center gap-2 mb-2">
                  <CalendarBlank size={16} />
                  As of Date
                </Label>
                <Input
                  id="tb-as-of"
                  type="date"
                  value={tbAsOfDate}
                  onChange={(e) => setTbAsOfDate(e.target.value)}
                  data-testid="tb-as-of-date"
                />
              </div>
              <Button onClick={fetchTrialBalance} className="gap-2" data-testid="tb-refresh-btn">
                <FileText size={16} />
                Refresh
              </Button>
            </div>
          </motion.div>

          <ReportToolbar
            title="Trial Balance"
            testidPrefix="trial-balance"
            downloadBaseName={`trial-balance-${tbAsOfDate}`}
            exportUrl={(fmt) => `${API_URL}/reports/trial-balance.${fmt}?as_of_date=${tbAsOfDate}`}
            onView={fetchTrialBalance}
          />

          {loading ? (
            <div className="flex justify-center py-12" data-testid="tb-loading">
              <CircleNotch size={32} className="animate-spin text-primary" />
            </div>
          ) : tbData ? (
            <div className="grid grid-cols-1 gap-6">
              <ChartCard title="Debit vs Credit by account type" subtitle="Click any row below for its ledger entries" testid="tb-type-chart">
                <TrendBarChart
                  isMonth={false}
                  xKey="type"
                  data={['asset', 'liability', 'equity', 'income', 'expense'].map((t) => ({
                    type: t.charAt(0).toUpperCase() + t.slice(1),
                    debit: (tbData.items || []).filter((i) => i.account_type === t).reduce((s, i) => s + i.debit, 0),
                    credit: (tbData.items || []).filter((i) => i.account_type === t).reduce((s, i) => s + i.credit, 0),
                  }))}
                  series={[{ key: 'debit', label: 'Debit', color: '#2563eb' }, { key: 'credit', label: 'Credit', color: '#d97706' }]}
                />
              </ChartCard>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border rounded-lg p-6"
                data-testid="tb-content"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading font-semibold">Trial Balance</h2>
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      tbData.is_balanced
                        ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/30'
                        : 'bg-rose-500/10 text-rose-700 border border-rose-500/30'
                    }`}
                    data-testid="tb-balanced-badge"
                  >
                    {tbData.is_balanced ? '✓ In Balance' : '⚠ Out of Balance'}
                  </span>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(tbData.items || []).map((item) => (
                      <TableRow
                        key={item.account_id || item.account_code}
                        className={item.account_id ? 'cursor-pointer hover:bg-muted/40' : ''}
                        onClick={() => item.account_id && setDrill({ category: item.account_type, accountId: item.account_id, endDate: tbAsOfDate, title: `${item.account_code} · ${item.account_name}` })}
                        data-testid={`tb-row-${item.account_code}`}
                      >
                        <TableCell className="font-mono text-sm">{item.account_code}</TableCell>
                        <TableCell>
                          <div>{item.account_name}</div>
                          <div className="text-xs text-muted-foreground capitalize">{item.account_type}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.debit > 0 ? formatCurrency(item.debit) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.credit > 0 ? formatCurrency(item.credit) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!tbData.items || tbData.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No account balances yet. Post some journal entries first.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableHeader>
                    <TableRow className="border-t-2">
                      <TableHead colSpan={2} className="font-bold">Totals</TableHead>
                      <TableHead className="text-right font-mono font-bold text-foreground" data-testid="tb-total-debit">
                        {formatCurrency(tbData.total_debit || 0)}
                      </TableHead>
                      <TableHead className="text-right font-mono font-bold text-foreground" data-testid="tb-total-credit">
                        {formatCurrency(tbData.total_credit || 0)}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>

                {tbData.uncategorized_amount > 0 && (
                  <div className="mt-4 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
                    ⚠ AED {Number(tbData.uncategorized_amount || 0).toLocaleString()} of journal activity is on accounts without a recognised type — please review the Chart of Accounts.
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-4">
                  As of {formatDate(tbData.as_of_date)}
                </p>
              </motion.div>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
      <DrilldownDialog request={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
